import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import MainAnimation from "@components/MainAnimation";
import dashboardService from "../services/dashboardService";
import MensalYearGraph from "@components/Dashboard/MensalYearGraph";
import historicalData from "../utils/data/ICEStocksHistorical2.json";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  Typography,
  Box,
} from "@material-ui/core";
import { ImgCotacao } from "../components/Feed/CotacaoInfo";
import { green, grey, red } from "@material-ui/core/colors";
import { useSelector } from "react-redux";
import WeatherAlerts from "../components/Dashboard/WeatherAlerts";
import IceStocksRobusta from "../components/Dashboard/IceStocksRobusta";
import Contratos from "../components/Dashboard/Contratos";
import { Watermark } from "../components/Dashboard/Contratos";
import mixpanel from "@utils/mixpanel/mixpanel.js";
import { RELATORIO_ICE_STOCKS_NY } from "../utils/mixpanel/mixpanelEvents";
import PendingSacksGraph from "../components/Dashboard/PendingSacksGraph";

let RED = red[400];

function Dashboard() {
  let [iceReports, setIceReports] = useState([]);
  const [user] = useSelector((state) => [state.authReducer.user]);
  let [loading, setLoading] = useState();
  const location = useLocation();
  const isIceStocksArabica = location.pathname.includes("/icestocks_arabica");
  const isIceStocksRobusta = location.pathname.includes("/icestocks_robusta");
  const isContratos = location.pathname.includes("/contratos");
  const isWeatherReports = location.pathname.includes("/alertas");

  useEffect(() => {
    mixpanel.track(RELATORIO_ICE_STOCKS_NY);
    setLoading(true);
    dashboardService
      .getIceReports(1)
      .then((rIceReports) => {
        setIceReports(rIceReports);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => setLoading(false));
  }, []);

  let reportsToShow =
    user.type === "A" ? iceReports : [...iceReports].slice(0, 10);
  let reversedReports = [...iceReports].reverse();
  let allReports = [...historicalData, ...reversedReports];
  return (
    <MainAnimation>
      <Container maxWidth="lg" style={{ paddingTop: 20, display: "flex" }}>
        <Paper style={{ width: "100%", padding: 24, minHeight: 300 }}>
          <Box sx={{ width: "100%", marginTop: 4 }}>
            {" "}
            {/* Aba 1 - Gráfico e ICE */}
            {isIceStocksArabica && (
              <Box mt={3}>
                {/* Tabela NY */}

                <TableContainer
                  component={Paper}
                  style={{ position: "relative" }}
                >
                  <Watermark />
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            <ImgCotacao />
                            ICE Stocks NY
                          </div>
                        </TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          Total Sacos
                        </TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          Variação Dia
                        </TableCell>{" "}
                        {/* Nova coluna */}
                        <TableCell style={{ textAlign: "center" }}>%</TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          Pendentes
                        </TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          Variação Dia
                        </TableCell>{" "}
                        <TableCell style={{ textAlign: "center" }}>%</TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          Aprovadas
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>%</TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          Reprovadas
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportsToShow?.map((row, index) => {
                        const next = reportsToShow[index + 1];

                        // % de variação
                        const bagsChange = (key) =>
                          next
                            ? getPercentageChange(row[key], next[key])
                            : null;

                        // Diferença em quantidade
                        const bagsDiff = (key) =>
                          next && row[key] != null && next[key] != null
                            ? row[key] - next[key]
                            : null;

                        const getColor = (val) => {
                          if (val == null) return undefined;
                          return parseFloat(val) < 0 ? RED : green[500];
                        };

                        const getApprovedFailed = (r, key) => {
                          if (!r.bagsFailedGrading || !r.bagsPassedGrading)
                            return null;
                          return (
                            (r[key] /
                              (r.bagsFailedGrading + r.bagsPassedGrading)) *
                            100
                          ).toFixed(2);
                        };

                        return (
                          <TableRow key={row._id}>
                            <TableCell>
                              {new Date(row.createdAt).toLocaleString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })}
                            </TableCell>

                            {/* Total Sacos */}
                            <TableCell style={{ textAlign: "right" }}>
                              {row.totalBagsCertified?.toLocaleString() ?? "—"}
                            </TableCell>
                            <TableCell
                              style={{
                                color: getColor(bagsDiff("totalBagsCertified")),
                                fontWeight: "bold",
                                textAlign: "right",
                              }}
                            >
                              {bagsDiff("totalBagsCertified") != null
                                ? (bagsDiff("totalBagsCertified") > 0
                                    ? "+"
                                    : "") +
                                  bagsDiff(
                                    "totalBagsCertified",
                                  ).toLocaleString()
                                : "—"}
                            </TableCell>
                            <TableCell
                              style={{
                                color: getColor(
                                  bagsChange("totalBagsCertified"),
                                ),
                                paddingLeft: 12,
                              }}
                            >
                              {bagsChange("totalBagsCertified") != null
                                ? `${bagsChange("totalBagsCertified")}%`
                                : "—"}
                            </TableCell>

                            {/* Pendentes */}
                            <TableCell style={{ textAlign: "right" }}>
                              {row.bagsPendingGrading?.toLocaleString() ?? "—"}
                            </TableCell>
                            <TableCell
                              style={{
                                color: getColor(bagsDiff("bagsPendingGrading")),
                                fontWeight: "bold",
                                textAlign: "right",
                              }}
                            >
                              {bagsDiff("bagsPendingGrading") != null
                                ? (bagsDiff("bagsPendingGrading") > 0
                                    ? "+"
                                    : "") +
                                  bagsDiff(
                                    "bagsPendingGrading",
                                  ).toLocaleString()
                                : "—"}
                            </TableCell>
                            <TableCell
                              style={{
                                color: getColor(
                                  bagsChange("bagsPendingGrading"),
                                ),
                                paddingLeft: 12,
                              }}
                            >
                              {bagsChange("bagsPendingGrading") != null
                                ? `${bagsChange("bagsPendingGrading")}%`
                                : "—"}
                            </TableCell>

                            {/* Aprovadas */}
                            <TableCell style={{ textAlign: "right" }}>
                              {row.bagsPassedGrading?.toLocaleString() ?? "—"}
                            </TableCell>
                            <TableCell style={{ paddingLeft: 12 }}>
                              {!!getApprovedFailed(row, "bagsPassedGrading")
                                ? `${getApprovedFailed(row, "bagsPassedGrading")}%`
                                : "—"}
                            </TableCell>

                            {/* Reprovadas */}
                            <TableCell style={{ textAlign: "right" }}>
                              {row.bagsFailedGrading?.toLocaleString() ?? "—"}
                            </TableCell>
                            <TableCell style={{ paddingLeft: 12 }}>
                              {!!getApprovedFailed(row, "bagsFailedGrading")
                                ? `${getApprovedFailed(row, "bagsFailedGrading")}%`
                                : "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <VariationTable iceReports={allReports} />
                <Box mt={5}>
                  <EChartsGradingChart data={allReports} />
                </Box>
                <MensalYearGraph data={allReports} />
                <PendingSacksGraph data={allReports} />
              </Box>
            )}
            {isIceStocksRobusta && (
              <IceStocksRobusta iceReports={reportsToShow} />
            )}
            {/* Aba 2 - Vencimentos */}
            {isContratos && <Contratos />}
          </Box>

          {isWeatherReports && <WeatherAlerts />}
        </Paper>
      </Container>
    </MainAnimation>
  );
}

export default Dashboard;

// Funções auxiliares
export const getPercentageChange = (current, previous) => {
  if (previous == null || previous === 0) return null;
  return (((current - previous) / previous) * 100).toFixed(2);
};

export const DifferenceCell = ({ iceReports, keyName, daysAgo = 1 }) => {
  if (!iceReports?.length) return <TableCell>-</TableCell>;

  const sortedReports = [...iceReports].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  // Get the most recent report that has a valid keyName value
  const latest = sortedReports.find((report) => report?.[keyName] != null);
  if (!latest) return <TableCell>-</TableCell>;

  const targetDate = new Date(latest.createdAt);
  targetDate.setDate(targetDate.getDate() - daysAgo);
  //console.log("targetDate", targetDate);
  // Find the closest report to the target date (excluding the `latest` report)
  const closestReport = sortedReports.reduce((closest, current) => {
    if (current._id === latest._id || current?.[keyName] == null)
      return closest;

    const currentDiff = Math.abs(new Date(current.createdAt) - targetDate);
    const closestDiff = closest
      ? Math.abs(new Date(closest.createdAt) - targetDate)
      : Infinity;

    return currentDiff < closestDiff ? current : closest;
  }, null);

  const latestValue = latest?.[keyName] ?? 0;
  const pastValue = closestReport?.[keyName] ?? 0;
  const diff = latestValue - pastValue;

  const getColor = () => {
    if (diff > 0) return green[500];
    if (diff < 0) return RED;
    return "inherit";
  };

  const getPrefix = () => {
    if (diff > 0) return "+";
    if (diff < 0) return "-";
    return "";
  };

  return (
    <TableCell style={{ color: getColor(), fontWeight: "bold" }} align="right">
      {getPrefix()}
      {Math.abs(diff).toLocaleString()}
    </TableCell>
  );
};

function getLastOfMonth(data) {
  // Sort by date ascending
  const sorted = [...data].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );

  const result = [];

  sorted.forEach((item) => {
    const date = new Date(item.createdAt);
    const monthYear = `${date.getMonth()}-${date.getFullYear()}`;

    // Always replace the last item for the current month
    if (
      result.length > 0 &&
      result[result.length - 1].monthYear === monthYear
    ) {
      result[result.length - 1] = { ...item, monthYear };
    } else {
      result.push({ ...item, monthYear });
    }
  });

  // Remove the extra monthYear property
  return result.map(({ monthYear, ...rest }) => rest);
}
function EChartsGradingChart({ data }) {
  let xData = getLastOfMonth(data);

  const formattedDates = xData?.map((item) => {
    const d = new Date(item.createdAt);
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  });

  const BLUE = "#6ecaff";
  const GRAY = grey[400];

  const option = {
    title: {
      text: `Total Sacos x Preco NY (cts/lb)`,
      left: "center",
      textStyle: { color: GRAY },
    },
    tooltip: { trigger: "axis" },
    legend: {
      top: 30,
      data: ["Certificados", "Preço NY"],
      textStyle: {
        color: GRAY,
      },
    },
    xAxis: { type: "category", data: formattedDates },
    yAxis: [
      {
        type: "value",
        name: "Total Sacos Certificados",
        position: "left",
        axisLine: { lineStyle: { color: BLUE } },
        axisLabel: { formatter: "{value}" },
        alignTicks: true, // <-- Add this
      },
      {
        type: "value",
        name: "Preço NY",
        position: "right",
        axisLine: { lineStyle: { color: GRAY } },
        axisLabel: { formatter: "{value}" },
        alignTicks: true, // <-- And this
      },
    ],
    series: [
      {
        name: "Certificados",
        type: "line",
        data: xData.map((d) =>
          d.totalBagsCertified === 0 ? null : d.totalBagsCertified,
        ),
        color: BLUE,
        connectNulls: true,
        yAxisIndex: 0,
      },
      {
        name: "Preço NY",
        type: "line",
        data: xData.map((d) => (d.priceArabica === 0 ? null : d.priceArabica)),
        color: GRAY,
        connectNulls: true,
        yAxisIndex: 1,
      },
    ],
    grid: { left: "10%", right: "10%", bottom: "15%" },
    toolbox: { feature: { saveAsImage: {}, dataZoom: {}, restore: {} } },
    dataZoom: [{ type: "slider", start: 0, end: 100 }],

    graphic: {
      elements: [
        {
          type: "text",
          left: "center",
          top: "center",
          style: {
            text: "cafetrade.com.br",
            fontSize: 30,
            fontWeight: "bold",
            fill: "rgba(200, 200, 200, 0.3)",
          },
          z: -10, // Send to back
        },
      ],
    },
  };

  return (
    <ReactECharts option={option} style={{ height: "700px", width: "100%" }} />
  );
}

let intervals = [1, 7, 30, 60];

export const VariationTable = ({ iceReports, isRobusta }) => {
  let totalBags = isRobusta ? "totalBagsRobusta" : "totalBagsCertified";
  return (
    <TableContainer component={Paper} style={{ maxWidth: 600, marginTop: 40 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Intervalo</TableCell>
            <TableCell align="right">Total Sacos</TableCell>
            {isRobusta ? null : (
              <>
                <TableCell />
                <TableCell align="right">Pendentes </TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {intervals.map((daysAgo) => (
            <TableRow key={`variation-${daysAgo}`}>
              <TableCell component="th" scope="row">
                Variação {daysAgo} dia{daysAgo === 1 ? `` : `s`}
              </TableCell>

              <DifferenceCell
                iceReports={iceReports}
                keyName={totalBags}
                daysAgo={daysAgo}
              />

              {isRobusta ? null : (
                <>
                  <TableCell align="center">→</TableCell>
                  <DifferenceCell
                    iceReports={iceReports}
                    keyName="bagsPendingGrading"
                    daysAgo={daysAgo}
                  />
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
